import React, { useState, useRef, useEffect } from "react";
import type { BasketItem } from "../page";
import { isSunmiDevice, printOnSunmi } from "../../../utils/sunmiPrinter";
import { toast } from "../../../components/toast";
import { roundMoney } from "../utils/money";
import { amountInWordsTaka } from "../../../utils/numberToWords";

interface PrintReceiptModalProps {
  onClose: () => void;
  paymentMethods?: Array<{ method: string; amount: number }>;
  actualPayments?: Array<{ method: string; amount: number }>;
  basketItems: BasketItem[];
  subtotal: number;
  discount: number;
  orderDiscount?: number;
  tax: number;
  total: number;
  appliedCreditMemo?: { amount: number } | null;
  receiptData?: {
    receiptNo?: string;
    transactionId?: string;
    saleDate?: string;
    cashier?: string;
    terminal?: string;
    storeName?: string;
    storeAddress?: string;
    customerName?: string;
    customerCode?: string | null;
    customerMobile?: string | null;
    binNumber?: string | null;
  };
}

export default function PrintReceiptModal({
  onClose,
  paymentMethods = [],
  actualPayments = [],
  basketItems,
  subtotal,
  orderDiscount = 0,
  appliedCreditMemo,
  receiptData,
}: PrintReceiptModalProps) {
  const [printType, setPrintType] = useState<"customer" | "merchant" | "both">(
    "customer",
  );
  const [copies, setCopies] = useState(1);
  const [isPrinting, setIsPrinting] = useState(false);
  const receiptPreviewRef = useRef<HTMLDivElement>(null);

  const printerConnected =
    isSunmiDevice() && (window.SunmiPrinter?.isConnected() ?? false);

  const toRateNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const parseDateSafe = (value: unknown): Date | null => {
    if (!value) return null;
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const getActiveItemDiscount = (item: BasketItem) => {
    const now = new Date();
    const discounts = Array.isArray(item?.discounts) ? item.discounts : [];
    return (
      discounts.find((discount) => {
        const from = parseDateSafe(
          discount?.effectiveFrom ??
            discount?.effectiveFromDate ??
            discount?.startDate ??
            discount?.fromDate ??
            discount?.validFrom,
        );
        const to = parseDateSafe(
          discount?.effectiveTo ??
            discount?.effectiveToDate ??
            discount?.endDate ??
            discount?.toDate ??
            discount?.validTo,
        );
        if (from && now < from) return false;
        if (to && now > to) return false;
        return true;
      }) ?? null
    );
  };

  const getItemDiscountAmount = (item: BasketItem): number => {
    const activeDiscount = getActiveItemDiscount(item);
    if (!activeDiscount) return 0;
    const lineBase = Number(item.price || 0) * Number(item.qty || 0);
    if (lineBase <= 0) return 0;
    const typeName = String(
      activeDiscount?.discountType?.name ??
        activeDiscount?.discountTypeName ??
        activeDiscount?.amountType?.name ??
        activeDiscount?.amountTypeName ??
        activeDiscount?.type ??
        activeDiscount?.valueType ??
        "",
    ).toLowerCase();
    const typeId = Number(
      activeDiscount?.discountType?.id ??
        activeDiscount?.discountTypeId ??
        activeDiscount?.amountType?.id ??
        activeDiscount?.amountTypeId ??
        -1,
    );
    const isPercentage =
      activeDiscount?.isPercentage === true ||
      typeId === 1 ||
      typeName.includes("percent");
    const discountValue = toRateNumber(
      activeDiscount?.percentage ??
        activeDiscount?.percent ??
        activeDiscount?.rate ??
        activeDiscount?.discountPercentage ??
        activeDiscount?.amount ??
        activeDiscount?.discountAmount ??
        activeDiscount?.value,
    );
    if (discountValue <= 0) return 0;
    let discountAmount = isPercentage
      ? lineBase * (discountValue / 100)
      : discountValue * Number(item.qty || 0);
    const maxValue = toRateNumber(
      activeDiscount?.maximumValue ??
        activeDiscount?.maximumDiscount ??
        activeDiscount?.maxDiscount ??
        activeDiscount?.maxValue ??
        activeDiscount?.maxAmount ??
        activeDiscount?.maximumAmount,
    );
    if (maxValue > 0) discountAmount = Math.min(discountAmount, maxValue);
    return Math.max(0, Math.min(discountAmount, lineBase));
  };

  const getItemVatRate = (item: BasketItem): number => {
    const taxes = Array.isArray(item?.taxes) ? item.taxes : [];
    const rate = taxes.reduce((sum, tax) => {
      const r = toRateNumber(
        tax?.rate ??
          tax?.taxRate ??
          tax?.percentage ??
          tax?.percent ??
          tax?.amount ??
          tax?.vatPercentage ??
          tax?.value ??
          tax?.tax?.rate ??
          tax?.tax?.amount,
      );
      return sum + Math.max(0, r);
    }, 0);
    return rate > 0 ? rate : 0;
  };

  const lineBreakdown = basketItems.map((item) => {
    const lineBase = Number(item.price || 0) * Number(item.qty || 0);
    const lineDiscount = getItemDiscountAmount(item);
    const vatRate = getItemVatRate(item);
    const taxableBase = Math.max(0, lineBase - lineDiscount);
    const lineVat = taxableBase * (vatRate / 100);
    return { ...item, lineDiscount, lineVat, vatRate };
  });

  const totalLineDiscount = lineBreakdown.reduce(
    (sum, item) => sum + item.lineDiscount,
    0,
  );
  const totalLineVat = lineBreakdown.reduce(
    (sum, item) => sum + item.lineVat,
    0,
  );
  const totalDiscount = totalLineDiscount + orderDiscount;

  const counterInfo = JSON.parse(
    localStorage.getItem("counterCheckedInInfo") || "{}",
  );
  const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
  const businessDetails = (() => {
    try { return JSON.parse(localStorage.getItem("businessDetails") || "{}"); } catch { return {}; }
  })();
  const bizData = businessDetails?.data ?? businessDetails;
  const logoUrl = bizData?.imagePath ?? null;
  const bizPhone = bizData?.phoneNumber ?? null;
  const bizCurrency = bizData?.currency?.isO_Code ?? "BDT";
  const bizAddress = (() => {
    const a = bizData?.addressDetail;
    if (!a) return "";
    return [a.addressLine1, a.addressLine2, a.city, a.district, a.division, a.country]
      .map((p) => (p && typeof p === "object" ? p.name : p))
      .filter((p) => p && p !== "string" && p !== "null")
      .join(", ");
  })();
  const outlet = (() => {
    try {
      return JSON.parse(localStorage.getItem("outlet") || "{}");
    } catch {
      return {};
    }
  })();

  const effectiveSaleDate = receiptData?.saleDate
    ? new Date(receiptData.saleDate)
    : new Date();

  const orderNo =
    receiptData?.receiptNo ?? receiptData?.transactionId ?? `POS-${Date.now()}`;
  const dateShort = effectiveSaleDate
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(/ /g, "-");
  const timeStr = effectiveSaleDate.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const cashier =
    receiptData?.cashier || userInfo?.name || userInfo?.email || "Cashier";
  const customerName = receiptData?.customerName || "Walk In Customer";
  const customerMobile = receiptData?.customerMobile || "-";
  const binNumber = receiptData?.binNumber ?? null;
  const outletName = counterInfo?.outletName ?? receiptData?.storeName ?? "";
  const storeAddress =
    bizAddress ||
    receiptData?.storeAddress ||
    outlet?.address ||
    outlet?.location ||
    "";
  const storePhone =
    bizPhone ??
    outlet?.phone ??
    outlet?.phoneNumber ??
    outlet?.contactNumber ??
    outlet?.mobileNumber ??
    null;
  const totalQty = basketItems.reduce(
    (sum, item) => sum + Number(item.qty || 0),
    0,
  );
  const fmt = (n: number) =>
    n.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const netPayable = subtotal - totalDiscount + totalLineVat;
  const netDue = roundMoney(netPayable);

  // Actual amount recorded against the order (capped at what was due),
  // not the raw tendered amount before change.
  const transactionPayments =
    actualPayments.length > 0 ? actualPayments : paymentMethods;

  const totalPaid = paymentMethods.reduce((sum, pm) => sum + pm.amount, 0);
  // Change counts the applied credit memo too, as the tender screen does
  const creditApplied = appliedCreditMemo?.amount ?? 0;
  const change = Math.max(0, totalPaid + creditApplied - netDue);
  // Payment Total mirrors the actual amounts applied to the order.
  const paymentTotal = transactionPayments.reduce((sum, pm) => sum + pm.amount, 0);

  // Pure black — thermal printers only support black
  const BLK = "color:#000";

  const buildReceiptHtml = (copyType: "customer" | "merchant") => {
    const isCustomer = copyType === "customer";

    const itemsHtml = lineBreakdown
      .map(
        (item, idx) => `
        <tr>
          <td style="padding:2px 2px 2px 0;vertical-align:top;font-size:10px;${BLK}">${idx + 1}</td>
          <td style="padding:2px 2px;vertical-align:top;${BLK}">
            <div style="font-size:10px;${BLK}">${item.isBogoFreeItem ? "(Promo) " : ""}${item.name}</div>
            ${item.code ? `<div style="font-size:9px;${BLK}">${item.code}</div>` : ""}
          </td>
          <td style="text-align:center;padding:2px 2px;vertical-align:top;font-size:10px;${BLK}">${item.qty.toFixed(3)}</td>
          <td style="text-align:right;white-space:nowrap;padding:2px 2px;vertical-align:top;font-size:10px;${BLK}">${fmt(item.price)}</td>
          <td style="text-align:right;white-space:nowrap;padding:2px 0 2px 2px;vertical-align:top;font-size:10px;${BLK}">${fmt(item.total)}</td>
        </tr>`,
      )
      .join("");

    const paymentInfoHtml = paymentMethods
      .map(
        (pm) =>
          `<tr><td style="padding:1px 0;font-weight:bold;font-size:11px;${BLK}">${pm.method}</td><td style="text-align:right;padding:1px 0;font-weight:bold;font-size:11px;${BLK}">${fmt(pm.amount)}</td></tr>`,
      )
      .join("");

    const creditAppliedHtml = appliedCreditMemo
      ? `<tr><td style="padding:1px 0;font-size:11px;${BLK}">Credit Applied</td><td style="text-align:right;padding:1px 0;font-size:11px;${BLK}">${fmt(appliedCreditMemo.amount)}</td></tr>`
      : "";

    const copyLabel = `<div style="font-size:11px;font-weight:bold;${BLK}">Thank you for shopping</div>
         <div style="font-size:10px;font-weight:bold;${BLK}">${isCustomer ? "Customer Copy" : "Merchant Copy"}</div>`;

    const watermarkHtml = appliedCreditMemo
      ? `<div class="wm" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-35deg);font-size:30px;font-weight:900;white-space:nowrap;pointer-events:none;z-index:0;letter-spacing:2px">EXCHANGED</div>`
      : "";

    return `
      <div style="position:relative;overflow:hidden;font-family:'Arial','Helvetica Neue',sans-serif;font-size:11px;line-height:1.3;max-width:300px;padding:0 8px;${BLK}">
        ${watermarkHtml}

        <div style="text-align:center;margin:0;padding:0">
          <div style="display:flex;align-items:center;justify-content:center">
            <img src="${logoUrl ?? `${window.location.origin}/biznify-logo.png`}" alt="Biznify" style="max-width:100%;max-height:80px;width:auto;height:auto;object-fit:contain;display:block" />
          </div>
          ${outletName ? `<div style="font-size:11px;font-weight:bold;margin-top:2px;${BLK}">${outletName}</div>` : ""}
          ${storeAddress ? `<div style="font-size:9px;font-weight:bold;${BLK}">${storeAddress}</div>` : ""}
          ${storePhone ? `<div style="font-size:9px;${BLK}">Phone : ${storePhone}</div>` : ""}
        </div>
        <table style="width:100%;border-collapse:collapse;font-size:9px;margin-top:2px;${BLK}">
          <tr>
            <td style="${BLK}">${binNumber ? `BIN : ${binNumber}` : ""}</td>
          </tr>
        </table>
        <div style="border-bottom:1px solid #999;margin:2px 0"></div>

        <table style="width:100%;border-collapse:collapse;font-size:10px;${BLK}">
          <tr><td colspan="2" style="font-weight:bold;${BLK}">Invoice No. : ${orderNo}</td></tr>
          <tr>
            <td style="font-weight:bold;${BLK}">Invoice Date : ${dateShort}</td>
            <td style="text-align:right;white-space:nowrap;font-weight:bold;${BLK}">${timeStr}</td>
          </tr>
          <tr><td colspan="2" style="${BLK}">Name : ${customerName}</td></tr>
          <tr><td colspan="2" style="${BLK}">Mobile : ${customerMobile}</td></tr>
          <tr><td colspan="2" style="font-weight:bold;${BLK}">Served by : ${cashier}</td></tr>
        </table>
        <div style="border-bottom:1px solid #999;margin:2px 0"></div>

        <table style="width:100%;border-collapse:collapse;table-layout:fixed;${BLK}">
          <colgroup>
            <col style="width:16px"/>
            <col/>
            <col style="width:36px"/>
            <col style="width:52px"/>
            <col style="width:56px"/>
          </colgroup>
          <thead>
            <tr style="border-bottom:1px solid #000">
              <th style="text-align:left;padding:2px 2px 2px 0;font-size:10px;font-weight:bold;${BLK}">SL</th>
              <th style="text-align:left;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">Description</th>
              <th style="text-align:center;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">Sqty</th>
              <th style="text-align:right;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">MRP</th>
              <th style="text-align:right;padding:2px 0 2px 2px;font-size:10px;font-weight:bold;${BLK}">Amount</th>
            </tr>
          </thead>
          <tbody>${itemsHtml}</tbody>
          <tfoot>
            <tr style="border-top:1px solid #000">
              <td colspan="2" style="padding:2px 2px 2px 0;font-size:10px;font-weight:bold;${BLK}">Sub Total :</td>
              <td style="text-align:center;padding:2px 2px;font-size:10px;font-weight:bold;${BLK}">${totalQty.toFixed(2)}</td>
              <td></td>
              <td style="text-align:right;padding:2px 0 2px 2px;font-size:10px;font-weight:bold;${BLK}">${fmt(subtotal)}</td>
            </tr>
          </tfoot>
        </table>
        <div style="border-bottom:1px solid #999;margin:2px 0"></div>

        <table style="width:100%;border-collapse:collapse;font-size:11px;${BLK}">
          <tr><td style="padding:1px 0;padding-left:70px;${BLK}">Discount :</td><td style="text-align:right;padding:1px 0;${BLK}">${fmt(totalDiscount)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;${BLK}">VAT :</td><td style="text-align:right;padding:1px 0;${BLK}">${fmt(totalLineVat)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;font-weight:bold;${BLK}">Net Amount :</td><td style="text-align:right;padding:1px 0;font-weight:bold;${BLK}">${fmt(netDue)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;${BLK}">Total Payment :</td><td style="text-align:right;padding:1px 0;${BLK}">${fmt(paymentTotal)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;${BLK}">Paid Amount :</td><td style="text-align:right;padding:1px 0;${BLK}">${fmt(totalPaid)}</td></tr>
          <tr><td style="padding:1px 0;padding-left:70px;font-weight:bold;${BLK}">Change Amount :</td><td style="text-align:right;padding:1px 0;font-weight:bold;${BLK}">${fmt(change)}</td></tr>
        </table>

        <div style="font-size:10px;margin:2px 0;${BLK}"><b>In words:</b> <i>${amountInWordsTaka(netDue)}</i></div>

        <div style="display:inline-block;font-size:11px;font-weight:bold;margin:2px 0 1px;padding-bottom:1px;border-bottom:1px solid #000;${BLK}">Payment Info</div>
        <table style="width:100%;border-collapse:collapse;font-size:11px;${BLK}">
          <tr style="border-bottom:1px solid #000">
            <td style="padding:1px 0;${BLK}">DESCRIPTION</td>
            <td style="text-align:right;padding:1px 0;${BLK}">AMOUNT</td>
          </tr>
          ${paymentInfoHtml}
          ${creditAppliedHtml}
        </table>

        <div style="text-align:center;font-size:10px;margin-top:4px;line-height:1.4;${BLK}">
          ${copyLabel}
        </div>
        <div style="text-align:right;font-size:9px;margin-top:4px;${BLK}">Powered by biznify.net</div>
      </div>`;
  };

  const handlePrint = async () => {
    if (isSunmiDevice()) {
      if (!receiptPreviewRef.current) return;
      if (!window.SunmiPrinter?.isConnected()) {
        toast.error(
          "Printer not ready. Please check the printer and try again.",
        );
        return;
      }
      setIsPrinting(true);
      try {
        if (printType === "both") {
          const customerEl = receiptPreviewRef.current!.querySelector(
            '[data-copy="customer"]',
          ) as HTMLElement | null;
          const merchantEl = receiptPreviewRef.current!.querySelector(
            '[data-copy="merchant"]',
          ) as HTMLElement | null;
          for (let i = 0; i < copies; i++)
            await printOnSunmi(customerEl ?? receiptPreviewRef.current!, 1);
          for (let i = 0; i < copies; i++)
            await printOnSunmi(merchantEl ?? receiptPreviewRef.current!, 1);
        } else {
          await printOnSunmi(receiptPreviewRef.current, copies);
        }
        onClose();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Print failed");
      }
      setIsPrinting(false);
      return;
    }

    setIsPrinting(true);

    const pages: Array<"customer" | "merchant"> = [];
    if (printType === "both") {
      for (let i = 0; i < copies; i++) pages.push("customer");
      for (let i = 0; i < copies; i++) pages.push("merchant");
    } else {
      for (let i = 0; i < copies; i++) pages.push(printType);
    }

    const pagesHtml = pages
      .map(
        (type, i) =>
          `<div class="page">${buildReceiptHtml(type)}</div>${i < pages.length - 1 ? '<div class="page-break"></div>' : ""}`,
      )
      .join("");

    const html = `<!DOCTYPE html><html><head>
      <title>Receipt - ${orderNo}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box;color:#000}
        html,body{background:#fff;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        body{font-family:'Arial','Helvetica Neue',sans-serif;font-size:11px}
        .page{max-width:300px;margin:0 auto;padding:12px}
        .page-break{page-break-after:always;break-after:page}
        .wm{color:rgba(0,0,0,0.55);-webkit-print-color-adjust:exact;print-color-adjust:exact}
        @media print{
          *{color:#000 !important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          .wm{color:rgba(0,0,0,0.55) !important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
          body{margin:0;padding:0}
          .page{padding:4px;max-width:100%}
          .page-break{page-break-after:always;break-after:page}
        }
      </style>
    </head>
    <body>
      ${pagesHtml}
      <script>
        window.onload = function() {
          setTimeout(function(){ window.print(); window.close(); }, 500);
        };
      </` + `script>
    </body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    setTimeout(() => {
      URL.revokeObjectURL(url);
      setIsPrinting(false);
      onClose();
    }, 2000);
    if (!win) setIsPrinting(false);
  };

  const handlePrintRef = useRef(handlePrint);
  useEffect(() => {
    handlePrintRef.current = handlePrint;
  });
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !isPrinting) handlePrintRef.current();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isPrinting]);

  const blk: React.CSSProperties = { color: "#000" };

  const renderReceiptPreview = (copyType: "customer" | "merchant") => {
    const isCustomer = copyType === "customer";

    return (
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          fontFamily: "'Arial', 'Helvetica Neue', sans-serif",
          fontSize: "11px",
          lineHeight: "1.3",
          color: "#000",
          paddingLeft: "8px",
          paddingRight: "8px",
        }}
      >
        {appliedCreditMemo && (
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            transform: "translate(-50%,-50%) rotate(-35deg)",
            fontSize: "38px", fontWeight: 900,
            color: "rgba(0,0,0,0.55)", whiteSpace: "nowrap",
            pointerEvents: "none", zIndex: 0, letterSpacing: "2px",
          }}>EXCHANGED</div>
        )}
        {/* Header */}
        <div style={{ textAlign: "center", margin: 0, paddingTop: "4px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img
              src={logoUrl ?? "/biznify-logo.png"}
              alt="Biznify"
              style={{
                maxWidth: "100%",
                maxHeight: "80px",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                display: "block",
              }}
            />
          </div>
          {outletName && (
            <div style={{ fontSize: "11px", fontWeight: "bold", marginTop: "2px", ...blk }}>
              {outletName}
            </div>
          )}
          {storeAddress && (
            <div style={{ fontSize: "9px", fontWeight: "bold", ...blk }}>{storeAddress}</div>
          )}
          {storePhone && (
            <div style={{ fontSize: "9px", ...blk }}>Phone : {storePhone}</div>
          )}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9px", marginTop: "2px", ...blk }}>
          <tbody>
            <tr>
              <td style={{ ...blk }}>{binNumber ? `BIN : ${binNumber}` : ""}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ borderBottom: "1px solid #999", margin: "2px 0" }} />

        {/* Invoice / Customer info */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "10px",
            ...blk,
          }}
        >
          <tbody>
            <tr>
              <td colSpan={2} style={{ fontWeight: "bold", ...blk }}>Invoice No. : {orderNo}</td>
            </tr>
            <tr>
              <td style={{ fontWeight: "bold", ...blk }}>Invoice Date : {dateShort}</td>
              <td style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: "bold", ...blk }}>
                {timeStr}
              </td>
            </tr>
            <tr>
              <td colSpan={2} style={{ ...blk }}>Name : {customerName}</td>
            </tr>
            <tr>
              <td colSpan={2} style={{ ...blk }}>Mobile : {customerMobile}</td>
            </tr>
            <tr>
              <td colSpan={2} style={{ fontWeight: "bold", ...blk }}>Served by : {cashier}</td>
            </tr>
          </tbody>
        </table>
        <div style={{ borderBottom: "1px solid #999", margin: "2px 0" }} />

        {/* Items table */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
            ...blk,
          }}
        >
          <colgroup>
            <col style={{ width: "16px" }} />
            <col />
            <col style={{ width: "36px" }} />
            <col style={{ width: "52px" }} />
            <col style={{ width: "56px" }} />
          </colgroup>
          <thead>
            <tr style={{ borderBottom: "1px solid #000" }}>
              {(["SL", "Description", "Sqty", "MRP", "Amount"] as const).map(
                (h, i) => (
                  <th
                    key={h}
                    style={{
                      textAlign:
                        i === 0 || i === 1
                          ? "left"
                          : i === 2
                            ? "center"
                            : "right",
                      padding: "2px 2px",
                      fontSize: "10px",
                      fontWeight: "bold",
                      ...blk,
                    }}
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {lineBreakdown.map((item, idx) => (
              <tr
                key={idx}
              >
                <td
                  style={{
                    padding: "2px 2px 2px 0",
                    verticalAlign: "top",
                    fontSize: "10px",
                    ...blk,
                  }}
                >
                  {idx + 1}
                </td>
                <td
                  style={{ padding: "2px 2px", verticalAlign: "top", ...blk }}
                >
                  <div style={{ fontSize: "10px", ...blk }}>
                    {item.isBogoFreeItem ? "(Promo) " : ""}
                    {item.name}
                  </div>
                  {item.code && (
                    <div style={{ fontSize: "9px", ...blk }}>
                      {item.code}
                    </div>
                  )}
                </td>
                <td
                  style={{
                    textAlign: "center",
                    padding: "2px 2px",
                    verticalAlign: "top",
                    fontSize: "10px",
                    ...blk,
                  }}
                >
                  {item.qty.toFixed(3)}
                </td>
                <td
                  style={{
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    padding: "2px 2px",
                    verticalAlign: "top",
                    fontSize: "10px",
                    ...blk,
                  }}
                >
                  {fmt(item.price)}
                </td>
                <td
                  style={{
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    padding: "2px 0 2px 2px",
                    verticalAlign: "top",
                    fontSize: "10px",
                    ...blk,
                  }}
                >
                  {fmt(item.total)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: "1px solid #000" }}>
              <td colSpan={2} style={{ padding: "2px 2px 2px 0", fontSize: "10px", fontWeight: "bold", ...blk }}>
                Sub Total :
              </td>
              <td style={{ textAlign: "center", padding: "2px 2px", fontSize: "10px", fontWeight: "bold", ...blk }}>
                {totalQty.toFixed(2)}
              </td>
              <td />
              <td style={{ textAlign: "right", padding: "2px 0 2px 2px", fontSize: "10px", fontWeight: "bold", ...blk }}>
                {fmt(subtotal)}
              </td>
            </tr>
          </tfoot>
        </table>
        <div style={{ borderBottom: "1px solid #999", margin: "2px 0" }} />

        {/* Totals */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "11px",
            ...blk,
          }}
        >
          <tbody>
            <tr>
              <td style={{ padding: "1px 0", paddingLeft: "70px", ...blk }}>Discount :</td>
              <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>
                {fmt(totalDiscount)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "1px 0", paddingLeft: "70px", ...blk }}>VAT :</td>
              <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>
                {fmt(totalLineVat)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "1px 0", paddingLeft: "70px", fontWeight: "bold", ...blk }}>Net Amount :</td>
              <td style={{ textAlign: "right", padding: "1px 0", fontWeight: "bold", ...blk }}>
                {fmt(netDue)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "1px 0", paddingLeft: "70px", ...blk }}>Total Payment :</td>
              <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>
                {fmt(paymentTotal)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "1px 0", paddingLeft: "70px", ...blk }}>Paid Amount :</td>
              <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>
                {fmt(totalPaid)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: "1px 0", paddingLeft: "70px", fontWeight: "bold", ...blk }}>Change Amount :</td>
              <td style={{ textAlign: "right", padding: "1px 0", fontWeight: "bold", ...blk }}>
                {fmt(change)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Amount in words */}
        <div style={{ fontSize: "10px", margin: "2px 0", ...blk }}>
          <b>In words:</b> <i>{amountInWordsTaka(netDue)}</i>
        </div>

        {/* Payment Info */}
        <div style={{ display: "inline-block", fontSize: "11px", fontWeight: "bold", margin: "2px 0 1px", paddingBottom: "1px", borderBottom: "1px solid #000", ...blk }}>
          Payment Info
        </div>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "11px",
            ...blk,
          }}
        >
          <tbody>
            <tr style={{ borderBottom: "1px solid #000" }}>
              <td style={{ padding: "1px 0", ...blk }}>DESCRIPTION</td>
              <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>AMOUNT</td>
            </tr>
            {paymentMethods.map((pm, idx) => (
              <tr key={idx}>
                <td style={{ padding: "1px 0", fontWeight: "bold", ...blk }}>{pm.method}</td>
                <td style={{ textAlign: "right", padding: "1px 0", fontWeight: "bold", ...blk }}>
                  {fmt(pm.amount)}
                </td>
              </tr>
            ))}
            {appliedCreditMemo && (
              <tr>
                <td style={{ padding: "1px 0", ...blk }}>Credit Applied</td>
                <td style={{ textAlign: "right", padding: "1px 0", ...blk }}>
                  {fmt(appliedCreditMemo.amount)}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Footer */}
        <div
          style={{
            textAlign: "center",
            fontSize: "10px",
            marginTop: "4px",
            lineHeight: "1.4",
            ...blk,
          }}
        >
          <div style={{ fontWeight: "bold", ...blk }}>Thank you for shopping</div>
          <div style={{ fontWeight: "bold", ...blk }}>
            {isCustomer ? "Customer Copy" : "Merchant Copy"}
          </div>
        </div>
        <div
          style={{
            textAlign: "right",
            fontSize: "9px",
            marginTop: "4px",
            ...blk,
          }}
        >
          Powered by biznify.net
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gray-800 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Print Receipt</h2>
            <p className="text-sm text-gray-300">Configure and preview</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-700 rounded-lg cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Left: receipt type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Receipt Type
              </label>
              <div className="space-y-2">
                {(["customer", "merchant", "both"] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setPrintType(type)}
                    className={`w-full p-3 rounded-lg border-2 transition-all cursor-pointer flex items-center gap-3 ${printType === type ? "border-teal-500 bg-teal-50" : "border-gray-200 hover:border-gray-300 bg-white"}`}
                  >
                    <div
                      className={`w-5 h-5 flex items-center justify-center rounded-full border-2 transition-colors ${printType === type ? "bg-teal-600 border-teal-600" : "border-gray-300"}`}
                    >
                      {printType === type && (
                        <i className="ri-check-line text-white text-xs"></i>
                      )}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">
                        {type === "customer"
                          ? "Customer Copy"
                          : type === "merchant"
                            ? "Merchant Copy"
                            : "Both Copies"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {type === "customer"
                          ? "Standard receipt for customer"
                          : type === "merchant"
                            ? "Store record copy"
                            : "Print customer and merchant"}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right: copies + printer status */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Number of Copies
              </label>
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setCopies(Math.max(1, copies - 1))}
                  className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-lg cursor-pointer"
                >
                  <i className="ri-subtract-line"></i>
                </button>
                <input
                  type="number"
                  onWheel={(e) => e.currentTarget.blur()}
                  value={copies}
                  onChange={(e) =>
                    setCopies(
                      Math.max(
                        1,
                        Math.min(5, parseInt(e.target.value, 10) || 1),
                      ),
                    )
                  }
                  className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg text-center text-lg font-bold focus:outline-none focus:border-teal-500"
                  min="1"
                  max="5"
                />
                <button
                  onClick={() => setCopies(Math.min(5, copies + 1))}
                  className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-lg cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                </button>
              </div>
              <div
                className={`mt-4 p-3 rounded-lg border ${printerConnected ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}`}
              >
                <div className="flex items-center gap-2">
                  <i
                    className={`ri-printer-line text-lg ${printerConnected ? "text-blue-600" : "text-red-500"}`}
                  ></i>
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${printerConnected ? "bg-green-500" : "bg-red-500"}`}
                    ></div>
                    <p
                      className={`text-xs ${printerConnected ? "text-blue-800" : "text-red-700"}`}
                    >
                      {printerConnected
                        ? "Printer Connected"
                        : "Printer Not Connected"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-bold text-gray-900 mb-3">
              Receipt Preview
            </h3>
            {printType === "both" ? (
              <div>
                <div className="flex items-center gap-2 mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <i className="ri-information-line text-blue-500"></i>
                  <p className="text-xs text-blue-700 font-medium">
                    2 receipts will be printed separately — one for customer,
                    one for merchant
                  </p>
                </div>
                <div ref={receiptPreviewRef} className="flex flex-col gap-6">
                  {(["customer", "merchant"] as const).map((copyType) => (
                    <div
                      key={copyType}
                      data-copy={copyType}
                      className="mx-auto"
                      style={{ width: "302px" }}
                    >
                      <div className="bg-white">
                        {renderReceiptPreview(copyType)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div
                ref={receiptPreviewRef}
                className="mx-auto"
                style={{ width: "302px" }}
              >
                <div className="bg-white">
                  {renderReceiptPreview(printType)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className={`px-8 py-3 rounded-lg font-bold transition-colors ${isPrinting ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-teal-600 hover:bg-teal-700 text-white cursor-pointer"}`}
          >
            {isPrinting ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Printing...
              </>
            ) : (
              <>
                <i className="ri-printer-line mr-2"></i>Print{" "}
                {copies > 1 ? `${copies} Copies` : "Receipt"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
